<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn icon @click="$router.go(-1)" class="mr-2">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h3 class="font-weight-bold">{{ wheelName }} - Reward Items</h3>
              <v-btn
                v-if="can('LUCKY_WHEELS.CREATE')"
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                @click="openCreateDialog"
              >
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Items Table -->
      <v-row v-else>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Status</th>
                      <th class="text-left">Reward Name</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Value</th>
                      <th class="text-left">Probability</th>
                      <th class="text-left">Color</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in items" :key="`item_${i}`">
                      <td>
                        <v-chip
                          small
                          :color="item.is_active ? 'success' : 'error'"
                          text-color="white"
                        >
                          {{ item.is_active ? "Active" : "Inactive" }}
                        </v-chip>
                      </td>
                      <td>
                        <div class="d-flex align-center">
                          <div
                            v-if="item.color"
                            :style="{
                              backgroundColor: item.color,
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              marginRight: '8px',
                            }"
                          ></div>
                          <strong>{{ item.reward_name }}</strong>
                        </div>
                      </td>
                      <td>
                        <v-chip small outlined>
                          {{ item.reward_type }}
                        </v-chip>
                      </td>
                      <td>
                        <span v-if="item.reward_type === 'coins'">
                          <v-icon small color="amber">mdi-coin</v-icon>
                          {{ item.reward_value }}
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td>
                        <v-chip small color="primary" outlined>
                          {{ item.probability }}
                        </v-chip>
                      </td>
                      <td>
                        <div class="d-flex align-center">
                          <div
                            v-if="item.color"
                            :style="{
                              backgroundColor: item.color,
                              width: '30px',
                              height: '30px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                            }"
                          ></div>
                          <span class="ml-2">{{ item.color || "-" }}</span>
                        </div>
                      </td>
                      <td class="text-right">
                        <v-btn
                          v-if="can('LUCKY_WHEELS.UPDATE')"
                          icon
                          small
                          color="primary"
                          @click="openEditDialog(item)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="can('LUCKY_WHEELS.DELETE')"
                          icon
                          small
                          color="error"
                          @click="confirmDelete(item)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div v-if="items.length === 0" class="text-center pa-8">
                <v-icon size="64" color="grey lighten-2">mdi-gift-off</v-icon>
                <p class="text-h6 mt-4 grey--text">No reward items yet</p>
                <v-btn color="primary" @click="openCreateDialog">
                  Add First Reward
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Create/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="primary white--text">
            <span class="text-h6">
              {{ editMode ? "Edit" : "Create" }} Reward Item
            </span>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model="formData.reward_name"
                label="Reward Name *"
                :rules="[rules.required]"
                outlined
                dense
                prepend-inner-icon="mdi-gift"
              ></v-text-field>

              <v-select
                v-model="formData.reward_type"
                :items="rewardTypes"
                label="Reward Type *"
                :rules="[rules.required]"
                outlined
                dense
                prepend-inner-icon="mdi-shape"
              ></v-select>

              <v-text-field
                v-model.number="formData.reward_value"
                label="Reward Value *"
                type="number"
                :rules="[rules.required, rules.number]"
                outlined
                dense
                prepend-inner-icon="mdi-coin"
                :disabled="formData.reward_type !== 'coins'"
                :hint="
                  formData.reward_type !== 'coins'
                    ? 'Only applicable for coins type'
                    : ''
                "
                persistent-hint
              ></v-text-field>

              <v-text-field
                v-model.number="formData.probability"
                label="Probability (Weight) *"
                type="number"
                :rules="[rules.required, rules.probability]"
                outlined
                dense
                prepend-inner-icon="mdi-percent"
                hint="Higher number = higher chance of winning"
                persistent-hint
              ></v-text-field>

              <v-text-field
                v-model="formData.color"
                label="Color (Hex Code)"
                outlined
                dense
                prepend-inner-icon="mdi-palette"
                placeholder="#FFD700"
              >
                <template v-slot:append>
                  <input
                    type="color"
                    v-model="formData.color"
                    style="
                      width: 40px;
                      height: 30px;
                      border: none;
                      cursor: pointer;
                    "
                  />
                </template>
              </v-text-field>

              <v-text-field
                v-model="formData.icon_url"
                label="Icon URL"
                outlined
                dense
                prepend-inner-icon="mdi-image"
              ></v-text-field>

              <v-switch
                v-model="formData.is_active"
                label="Active"
                color="primary"
                inset
              ></v-switch>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Cancel</v-btn>
            <v-btn color="primary" :loading="saving" @click="saveItem">
              {{ editMode ? "Update" : "Create" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title class="error white--text">
            <v-icon color="white" left>mdi-alert</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="pt-4">
            Are you sure you want to delete this reward item?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" @click="deleteItem">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "LuckyWheelItemsPage",
  data() {
    return {
      isLoading: false,
      items: [],
      wheelId: null,
      wheelName: "",
      dialog: false,
      deleteDialog: false,
      editMode: false,
      valid: false,
      saving: false,
      deleting: false,
      formData: {
        id: null,
        lucky_wheel_id: null,
        reward_name: "",
        reward_type: "coins",
        reward_value: 0,
        probability: 10,
        color: "#FFD700",
        icon_url: "",
        is_active: true,
      },
      selectedItem: null,
      rewardTypes: [
        { text: "Coins", value: "coins" },
        { text: "Try Again", value: "try_again" },
        { text: "Other", value: "other" },
      ],
      rules: {
        required: (v) => !!v || "This field is required",
        number: (v) => v >= 0 || "Must be a positive number",
        probability: (v) => (v > 0 && v <= 100) || "Must be between 1 and 100",
      },
    };
  },
  mounted() {
    this.wheelId = this.$route.params.id;
    this.getData();
    this.getWheelInfo();
  },
  methods: {
    async getWheelInfo() {
      try {
        const response = await axios.get(`/lucky-wheels/${this.wheelId}`);
        console.log("lucky-wheels", response);

        if (response.data.success) {
          this.wheelName = response.data.data.name;
        }
      } catch (error) {
        console.error("Error fetching wheel info:", error);
      }
    },
    async getData() {
      this.isLoading = true;
      try {
        const response = await axios.get("/lucky-wheel-items", {
          params: { lucky_wheel_id: this.decryptIt(this.wheelId) },
        });
        console.log("response", response);

        if (response.data.success) {
          this.items = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.editMode = false;
      this.formData = {
        id: null,
        lucky_wheel_id: this.decryptIt(this.wheelId),
        reward_name: "",
        reward_type: "coins",
        reward_value: 0,
        probability: 10,
        color: "#FFD700",
        icon_url: "",
        is_active: true,
      };
      this.dialog = true;
    },
    openEditDialog(item) {
      this.editMode = true;
      this.formData = {
        id: item.id,
        lucky_wheel_id: this.decryptIt(this.wheelId),
        reward_name: item.reward_name,
        reward_type: item.reward_type,
        reward_value: item.reward_value,
        probability: item.probability,
        color: item.color,
        icon_url: item.icon_url,
        is_active: item.is_active,
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    async saveItem() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        let response;
        
        if (this.editMode) {
          response = await axios.patch(
            `/lucky-wheel-items/${this.encryptIt(this.formData.id)}`,
            this.formData
          );
          console.log("formData", this.formData);
        } else {
          response = await axios.post("/lucky-wheel-items", this.formData);
        }

        if (response.data.success) {
          this.closeDialog();
          this.getData();
        }
      } catch (error) {
        console.error("Error saving item:", error);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },
    async deleteItem() {
      this.deleting = true;
      try {
        const response = await axios.delete(
          `/lucky-wheel-items/${this.encryptIt(this.selectedItem.id)}`
        );

        if (response.data.success) {
          this.deleteDialog = false;
          this.getData();
        }
      } catch (error) {
        console.error("Error deleting item:", error);
      } finally {
        this.deleting = false;
      }
    },
  },
  watch: {
    "formData.reward_type"(newType) {
      if (newType !== "coins") {
        this.formData.reward_value = 0;
      }
    },
  },
};
</script>
